import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../App";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { Button, ButtonLoading } from "../../SharedComponents/Button/Button";
import {
  EditableInformation,
  EditableInformationItemWrapper,
  EditableInformationTextInput,
  EditableInformationTextSelect,
} from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import "./AdminAccountsNew.css";

const queryCountries = (countries, query) => {
  return countries.filter((country) =>
    country.name.toLowerCase().includes(query.toLowerCase())
  );
};

export const AdminAccountsNew = () => {
  const { t } = useTranslation();
  const history = useHistory();
  /** @type {*} */
  const params = useParams();

  const primaryAccount = Cookies.get("primary_account");

  const accessLevel = Cookies.get("access_level");

  const { data: accountsData } = useQuery(getDistributorsQuery);
  const { data: countryData } = useQuery(countriesQuery);
  const [createAccount, { loading: createLoading }] = useMutation(
    createAccountMutation
  );

  const myCountry = accountsData?.distributors?.find(
    (dist) => dist.id === primaryAccount
  );

  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState("");

  const handleCountrySearch = (e) => {
    setCountryQuery(e.target.value);
  };

  useEffect(() => {
    if (countryQuery === "") {
      setFilteredCountries(countryData?.countries);
      return;
    }
    const filteredCountries = queryCountries(
      countryData?.countries,
      countryQuery
    );
    setFilteredCountries(filteredCountries);
  }, [countryQuery, countryData]);

  return (
    <div className="AdminDistributorEdit">
      <Formik
        initialValues={{
          name: "",
          email: "",
          clientId: "",
          vat: "",
          address: "",
          postalCode: "",
          city: "",
          country: myCountry?.country || "",
          parentId: params?.id
            ? params.id
            : primaryAccount
            ? primaryAccount
            : null,
        }}
        onSubmit={(data) => {
          createAccount({
            variables: {
              ...data,
            },
          })
            .then(() => {
              toast(t("toasts.accountCreated"), toastSuccessOptions);
              history.goBack();
            })
            .catch((err) => {
              handleGraphQLErrors(err);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form style={{ display: "flex", width: "100%" }}>
            <div className="AdminDistributorEdit__content">
              <EditHeader
                type="types.account"
                name={"accounts.new"}
                status={null}
                goBack={() => history.goBack()}
              />
              <div className="AdminDistributorEdit__companyinfo">
                <EditableInformation title="Company informations">
                  <EditableInformationItemWrapper label="accounts.name">
                    <EditableInformationTextInput
                      name="name"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="generic.email">
                    <EditableInformationTextInput
                      name="email"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.vat">
                    <EditableInformationTextInput
                      name="vat"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.address">
                    <EditableInformationTextInput
                      name="address"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.postalCode">
                    <EditableInformationTextInput
                      name="postalCode"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.city">
                    <EditableInformationTextInput
                      name="city"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.country">
                    <EditableInformationTextSelect
                      name="country"
                      type="text"
                      defaultEditingState={true}
                      options={
                        filteredCountries
                          ? filteredCountries.map((country) => {
                              return {
                                label: country.name,
                                value: country.name,
                              };
                            })
                          : []
                      }
                      withSearch={true}
                      query={countryQuery}
                      onQueryChange={(e) => handleCountrySearch(e)}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.clientId">
                    <EditableInformationTextInput
                      name="clientId"
                      type="text"
                      defaultEditingState={true}
                    />
                  </EditableInformationItemWrapper>

                  {accessLevel === "SUPERADMIN" && (
                    <EditableInformationItemWrapper label="accounts.distributor">
                      <EditableInformationTextSelect
                        defaultEditingState={true}
                        options={
                          accountsData?.distributors?.map((dist) => ({
                            label: dist.name,
                            value: dist.id,
                          })) || []
                        }
                        name="parentId"
                        type="text"
                      />
                    </EditableInformationItemWrapper>
                  )}
                </EditableInformation>
              </div>
            </div>
            <div className="AdminDistributorEdit__right">
              <div className="AdminDistributorEdit__right--actions">
                <Button
                  buttonStyle="Button__primary"
                  disabled={!isValid || !dirty}
                  type="submit"
                >
                  {createLoading ? <ButtonLoading /> : t("generic.save")}
                </Button>
                <Button
                  buttonStyle="Button__secondary"
                  click={(e) => {
                    e.preventDefault();
                    history.goBack();
                  }}
                >
                  {t("generic.cancel")}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const createAccountMutation = gql`
  mutation createSSOAccount(
    $clientId: String
    $name: String
    $email: String
    $vat: String
    $address: String
    $postalCode: String
    $city: String
    $country: String
    $parentId: String
  ) {
    createSSOAccount(
      clientId: $clientId
      name: $name
      email: $email
      vat: $vat
      address: $address
      postalCode: $postalCode
      city: $city
      country: $country
      role: "CUSTOMER"
      parentId: $parentId
    ) {
      id
      clientId
      name
      email
      vat
      address
      postalCode
      city
      country
      createdAt
      updatedAt
      role
      parentId
      suspendedAt
      status
      information
    }
  }
`;

const getDistributorsQuery = gql`
  query distributors {
    distributors {
      id
      name
      country
    }
  }
`;

const countriesQuery = gql`
  query {
    countries {
      id
      name
      code
    }
  }
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
  email: Yup.string().email().required("You must enter an e-mail"),
  vat: Yup.string().required("You must enter a VAT code"),
  address: Yup.string().required("You must enter an address"),
  postalCode: Yup.string().required("You must enter a postal code"),
  city: Yup.string().required("You must enter a city"),
  country: Yup.string().required("You must select a country"),
  clientId: Yup.string().required("You must enter a client id"),
  parentId: Yup.string().required("You must select an owner"),
});
